import { CURRENT_USER_FALLBACK, MOCK_FRIENDS } from "./users";

/**
 * Workout post shape (mirrors the documented `WorkoutPost` API contract —
 * see docs/home-redesign/DESIGN.md §9):
 *
 *   {
 *     id, user: PostAuthor, createdAt: ISOString,
 *     title, muscleGroups: string[],
 *     stats: { durationMin, volumeKg, exerciseCount, setCount, calories, prCount },
 *     exercises: [{ name, sets: [{ weight, reps, isPR }] }],
 *     achievements: [{ type, label }],
 *     imageUrl: string | null,
 *     likeCount, commentCount, shareCount,
 *     isLiked, isSaved, isMine,
 *   }
 */

const WORKOUT_TEMPLATES = [
  {
    title: "Push Day",
    muscleGroups: ["Chest", "Triceps", "Shoulders"],
    exercises: [
      "Bench Press",
      "Incline Dumbbell Press",
      "Cable Fly",
      "Overhead Press",
      "Lateral Raise",
      "Triceps Pushdown",
    ],
  },
  {
    title: "Pull Day",
    muscleGroups: ["Back", "Biceps"],
    exercises: [
      "Deadlift",
      "Pull-Up",
      "Barbell Row",
      "Lat Pulldown",
      "Face Pull",
      "Barbell Curl",
    ],
  },
  {
    title: "Leg Day",
    muscleGroups: ["Quads", "Hamstrings", "Glutes"],
    exercises: [
      "Back Squat",
      "Romanian Deadlift",
      "Leg Press",
      "Walking Lunge",
      "Leg Curl",
      "Calf Raise",
    ],
  },
  {
    title: "Upper Body",
    muscleGroups: ["Chest", "Back", "Arms"],
    exercises: [
      "Incline Barbell Press",
      "Seated Cable Row",
      "Dumbbell Shoulder Press",
      "Hammer Curl",
      "Skull Crusher",
    ],
  },
  {
    title: "Full Body",
    muscleGroups: ["Full Body"],
    exercises: [
      "Front Squat",
      "Bench Press",
      "Barbell Row",
      "Plank",
      "Kettlebell Swing",
    ],
  },
  {
    title: "Arms & Shoulders",
    muscleGroups: ["Arms", "Shoulders"],
    exercises: [
      "Overhead Press",
      "Barbell Curl",
      "Triceps Dip",
      "Lateral Raise",
      "Preacher Curl",
    ],
  },
  {
    title: "Core & Cardio",
    muscleGroups: ["Core", "Cardio"],
    exercises: ["Hanging Leg Raise", "Cable Crunch", "Row Sprint", "Plank", "Mountain Climber"],
  },
];

const ACHIEVEMENT_POOL = [
  { type: "streak", label: "10 Day Streak" },
  { type: "milestone", label: "Workout #100" },
  { type: "volume", label: "Highest Volume Ever" },
  { type: "duration", label: "Longest Workout" },
];

// Small seeded PRNG so the feed is stable across re-renders within a
// session, without needing an external dependency.
function createRng(seed) {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

function pick(rng, arr) {
  return arr[Math.floor(rng() * arr.length)];
}

function buildExerciseSets(rng, baseWeight, isFirstExercise) {
  const setCount = 3 + Math.floor(rng() * 2); // 3-4 sets
  const sets = [];
  let weight = baseWeight;
  for (let i = 0; i < setCount; i += 1) {
    const reps = 12 - i * 2 - Math.floor(rng() * 2);
    const isPR = isFirstExercise && i === setCount - 1 && rng() > 0.55;
    sets.push({ weight, reps: Math.max(4, reps), isPR });
    weight += Math.round(rng() * 8);
  }
  return sets;
}

function buildPost(rng, index, authors) {
  const template = pick(rng, WORKOUT_TEMPLATES);
  const author = pick(rng, authors);
  const isMine = author.id === CURRENT_USER_FALLBACK.id;

  const exercises = template.exercises.map((name, i) =>
    ({ name, sets: buildExerciseSets(rng, 20 + Math.floor(rng() * 90), i === 0) }),
  );

  const prCount = exercises.reduce(
    (n, ex) => n + ex.sets.filter((s) => s.isPR).length,
    0,
  );

  const volumeKg = exercises.reduce(
    (sum, ex) =>
      sum + ex.sets.reduce((s, set) => s + set.weight * set.reps, 0),
    0,
  );
  const setCount = exercises.reduce((n, ex) => n + ex.sets.length, 0);
  const durationMin = 35 + Math.floor(rng() * 70);

  const achievements = [];
  if (prCount > 0) {
    achievements.push({
      type: "pr",
      label: `New ${exercises[0].name} PR`,
    });
  }
  if (rng() > 0.75) achievements.push(pick(rng, ACHIEVEMENT_POOL));

  const hoursAgo = index === 0 ? 0.2 : index * (3 + rng() * 5);
  const createdAt = new Date(Date.now() - hoursAgo * 3_600_000).toISOString();

  return {
    id: `post-${index}`,
    user: author,
    createdAt,
    title: template.title,
    muscleGroups: template.muscleGroups,
    stats: {
      durationMin,
      volumeKg: Math.round(volumeKg),
      exerciseCount: exercises.length,
      setCount,
      calories: Math.round(durationMin * (6 + rng() * 3)),
      prCount,
    },
    exercises,
    achievements,
    imageUrl: null, // no photo upload yet — PostImage renders an auto-generated summary card
    likeCount: Math.floor(rng() * 40) + (isMine ? 180 : 20),
    commentCount: Math.floor(rng() * 30),
    shareCount: Math.floor(rng() * 15),
    isLiked: rng() > 0.6,
    isSaved: rng() > 0.8,
    isMine,
  };
}

export function generateWorkoutPosts(count = 48, seed = 42) {
  const rng = createRng(seed);
  const authors = [CURRENT_USER_FALLBACK, ...MOCK_FRIENDS];
  const posts = Array.from({ length: count }, (_, i) => buildPost(rng, i, authors));
  return posts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}
